import React, { useState, useEffect } from 'react';
import {
  Text,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Select,
  RadioGroup,
  Radio,
  NumberInput,
  NumberInputField,
  Alert,
  AlertIcon,
  AlertDescription,
  Divider,
  Box,
  FormErrorMessage,
  Checkbox,
} from '@chakra-ui/react';

import Card from 'components/card/Card';
import SelectSearch from 'components/fields/SelectSearch';
import DateField from 'components/fields/DateField';
import { PROVINCES } from 'variables/options';
import { useAppSelector, useAppDispatch } from 'hooks';
import { useSpecialtyCarriers } from 'hooks/index';
import {
  setInsuranceCompany,
  setEffectiveDate,
  setBinderPolicyNumber,
  setBusinessType,
  setPremium,
  setPremiumConfirmed,
  setClientName,
  setCustomerCode,
  setLOAPriorBroker,
  setLOAPriorPolicyNumber,
  setPaymentSchedule,
  setPaymentMethod,
  setBillingProvince,
  setBusinessCode,
  setCompanyFees,
  setBrokerFees,
  setTaxes,
} from 'reducers/specialtyFormReducer';

// Agency Bill ONLY markets — Direct Bill radio is disabled
// Note: bare 'Agile' intentionally excluded; "Agile Guardian Risk" is both-available
const AGENCY_BILLING_MARKETS = [
  'Premier Marine',
  'April Marine',
  'Forward Insurance',
  'Coast Underwriters',
  'Oasis Outdoor Adventure',
  'Agile UW', // Agile UW – Home/HABL only; does not match "Agile Guardian Risk"
  'HABL',
  'PAL',
  'Orbit',
  'Aviva Elite Special Events',
  'GroupOne',
];

// Direct Bill ONLY markets — Agency Bill radio is disabled
// Note: bare 'Aviva Elite' excluded to avoid matching "Orbit – Aviva Elite Special Events"
const DIRECT_BILLING_MARKETS = [
  'Apollo',
  'Hagerty',
  'Watercraft', // matches "Aviva Elite – Watercraft, Rec Vehicles, Trailers"
  'Rec Vehicles',
  'Trailers',
];

interface SpecialtyPolicyInformationProps {
  isLocked?: boolean;
  canEditPremium?: boolean;
}

const SpecialtyPolicyInformation = ({
  isLocked = false,
  canEditPremium = false,
}: SpecialtyPolicyInformationProps) => {
  const dispatch = useAppDispatch();
  const lineOfBusiness = useAppSelector(
    (state) => state.specialtyForm.lineOfBusiness,
  );
  const { availableCarriers, missingCarriers, isLoading } =
    useSpecialtyCarriers(lineOfBusiness || undefined);

  const insuranceCompany = useAppSelector(
    (state) => state.specialtyForm.data.insurance_company,
  );
  const effectiveDate = useAppSelector(
    (state) => state.specialtyForm.data.effective_date,
  );
  const binderPolicyNumber = useAppSelector(
    (state) => state.specialtyForm.data.binder_policy_number,
  );
  const businessType = useAppSelector(
    (state) => state.specialtyForm.data.business_type,
  );
  const premium = useAppSelector((state) => state.specialtyForm.data.premium);
  const premiumConfirmed = useAppSelector(
    (state) => state.specialtyForm.premiumConfirmed,
  );
  const clientName = useAppSelector(
    (state) => state.specialtyForm.data.client_name,
  );
  const customerCode = useAppSelector(
    (state) => state.specialtyForm.data.customer_code,
  );
  const isLOA = useAppSelector(
    (state) => state.specialtyForm.data.policy_information.is_loa,
  );
  const loaPriorBroker = useAppSelector(
    (state) => state.specialtyForm.data.policy_information.loa_prior_broker,
  );
  const loaPriorPolicyNumber = useAppSelector(
    (state) =>
      state.specialtyForm.data.policy_information.loa_prior_policy_number,
  );
  const billing = useAppSelector((state) => state.specialtyForm.data.billing);
  const validationErrors = useAppSelector(
    (state) => state.specialtyForm.validationErrors,
  );

  // Check if selected carrier requires agency billing
  const requiresAgencyBilling = React.useMemo(() => {
    if (!insuranceCompany) return false;
    return AGENCY_BILLING_MARKETS.some((market) =>
      insuranceCompany.toLowerCase().includes(market.toLowerCase()),
    );
  }, [insuranceCompany]);

  // Check if selected carrier requires direct billing
  const requiresDirectBilling = React.useMemo(() => {
    if (!insuranceCompany) return false;
    return DIRECT_BILLING_MARKETS.some((market) =>
      insuranceCompany.toLowerCase().includes(market.toLowerCase()),
    );
  }, [insuranceCompany]);

  // Auto-select agency billing for agency-only markets
  useEffect(() => {
    if (
      requiresAgencyBilling &&
      !requiresDirectBilling &&
      billing.payment_method !== 'agency_bill'
    ) {
      dispatch(setPaymentMethod('agency_bill'));
    }
  }, [
    requiresAgencyBilling,
    requiresDirectBilling,
    billing.payment_method,
    dispatch,
  ]);

  // Auto-select direct billing for direct-only markets
  useEffect(() => {
    if (
      requiresDirectBilling &&
      !requiresAgencyBilling &&
      billing.payment_method !== 'direct_bill'
    ) {
      dispatch(setPaymentMethod('direct_bill'));
    }
  }, [
    requiresDirectBilling,
    requiresAgencyBilling,
    billing.payment_method,
    dispatch,
  ]);

  const handleInsuranceCompanyChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    dispatch(setInsuranceCompany(event.target.value));
  };

  const handleClientNameChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    dispatch(setClientName(event.target.value));
  };

  const handleCustomerCodeChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    dispatch(setCustomerCode(event.target.value));
  };

  const handleEffectiveDateChange = (date: Date | null) => {
    if (date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      dispatch(setEffectiveDate(`${year}-${month}-${day}`));
    } else {
      dispatch(setEffectiveDate(''));
    }
  };

  const parseEffectiveDate = (dateString: string): Date | null => {
    if (!dateString) return null;
    const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return null;
    return new Date(
      parseInt(match[1]),
      parseInt(match[2]) - 1,
      parseInt(match[3]),
    );
  };

  const handleBinderPolicyNumberChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    dispatch(setBinderPolicyNumber(event.target.value));
  };

  // Local state for decimal inputs
  const [premiumInput, setPremiumInput] = useState<string>('');
  const [companyFeesInput, setCompanyFeesInput] = useState<string>('');
  const [brokerFeesInput, setBrokerFeesInput] = useState<string>('');
  const [taxesInput, setTaxesInput] = useState<string>('');

  // Sync local state with Redux state
  useEffect(() => {
    setPremiumInput(premium ? premium.toString() : '');
  }, [premium]);

  useEffect(() => {
    setCompanyFeesInput(
      billing.company_fees ? billing.company_fees.toString() : '',
    );
  }, [billing.company_fees]);

  useEffect(() => {
    setBrokerFeesInput(
      billing.broker_fees ? billing.broker_fees.toString() : '',
    );
  }, [billing.broker_fees]);

  useEffect(() => {
    setTaxesInput(billing.taxes ? billing.taxes.toString() : '');
  }, [billing.taxes]);

  const handleBusinessTypeChange = (value: 'new' | 'remarket') => {
    dispatch(setBusinessType(value));
  };

  const handlePremiumChange = (valueString: string) => {
    setPremiumInput(valueString);
    if (valueString === '') {
      dispatch(setPremium(0));
      return;
    }
    const value = parseFloat(valueString);
    if (!isNaN(value)) {
      dispatch(setPremium(value));
    }
  };

  const handleLOAPriorBrokerChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    dispatch(setLOAPriorBroker(event.target.value));
  };

  const handleLOAPriorPolicyNumberChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    dispatch(setLOAPriorPolicyNumber(event.target.value));
  };

  const handlePaymentScheduleChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    dispatch(setPaymentSchedule(event.target.value));
  };

  const handlePaymentMethodChange = (value: 'agency_bill' | 'direct_bill') => {
    dispatch(setPaymentMethod(value));
  };

  const handleBillingProvinceChange = (value: string | number | null) => {
    dispatch(setBillingProvince((value as string) || ''));
  };

  const handleBusinessCodeChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    dispatch(setBusinessCode(event.target.value));
  };

  const handleCompanyFeesChange = (valueString: string) => {
    setCompanyFeesInput(valueString);
    if (valueString === '') {
      dispatch(setCompanyFees(0));
      return;
    }
    const value = parseFloat(valueString);
    if (!isNaN(value)) {
      dispatch(setCompanyFees(value));
    }
  };

  const handleBrokerFeesChange = (valueString: string) => {
    setBrokerFeesInput(valueString);
    if (valueString === '') {
      dispatch(setBrokerFees(0));
      return;
    }
    const value = parseFloat(valueString);
    if (!isNaN(value)) {
      dispatch(setBrokerFees(value));
    }
  };

  const handleTaxesChange = (valueString: string) => {
    setTaxesInput(valueString);
    if (valueString === '') {
      dispatch(setTaxes(0));
      return;
    }
    const value = parseFloat(valueString);
    if (!isNaN(value)) {
      dispatch(setTaxes(value));
    }
  };

  return (
    <Card mb="20px" id="sectionSpecialtyPolicyInformation">
      <Text fontWeight="bold" fontSize="2xl" mb="20px">
        Policy Information
      </Text>

      <VStack spacing={4} align="stretch">
        {missingCarriers.length > 0 && (
          <Alert status="warning">
            <AlertIcon />
            <AlertDescription>
              {missingCarriers.length === 1
                ? `${missingCarriers[0].displayName} is not configured in your organization's carriers list. Please contact your administrator to add it.`
                : (() => {
                    const names = missingCarriers.map((c) => c.displayName);
                    if (names.length === 2) {
                      return `${names[0]} and ${names[1]} are not configured in your organization's carriers list. Please contact your administrator to add them.`;
                    }
                    const lastCarrier = names.pop();
                    return `${names.join(
                      ', ',
                    )}, and ${lastCarrier} are not configured in your organization's carriers list. Please contact your administrator to add them.`;
                  })()}
            </AlertDescription>
          </Alert>
        )}

        <HStack spacing={4} align="flex-end">
          <FormControl
            flex={2}
            isRequired
            isInvalid={!!validationErrors?.insurance_company}
          >
            <FormLabel ml="0">Insurance Company on Application</FormLabel>
            <Select
              variant="main"
              name="insurance_company"
              value={insuranceCompany}
              onChange={handleInsuranceCompanyChange}
              placeholder="Select Insurance Company"
              bg="white"
              borderColor={!insuranceCompany ? 'red' : 'gray.300'}
              isDisabled={
                isLocked || isLoading || availableCarriers.length === 0
              }
              _focus={{
                borderColor: 'blue.500',
                boxShadow: '0 0 0 1px blue.500',
              }}
            >
              {availableCarriers.map((carrier) => (
                <option
                  key={carrier.dbCarrier?.id || carrier.name}
                  value={carrier.name}
                >
                  {carrier.displayName}
                </option>
              ))}
            </Select>
            {validationErrors?.insurance_company && (
              <FormErrorMessage>
                {validationErrors.insurance_company}
              </FormErrorMessage>
            )}
            {!isLoading && availableCarriers.length === 0 && (
              <Text fontSize="sm" color="red.500" mt="5px">
                No specialty carriers are available. Please contact your
                administrator.
              </Text>
            )}
          </FormControl>

          {/* Only show Business Type for non-LOA submissions */}
          {!isLOA && (
            <FormControl
              flex={1}
              isRequired
              isInvalid={!!validationErrors?.business_type}
            >
              <FormLabel ml="0">Business Type</FormLabel>
              <RadioGroup
                value={businessType}
                onChange={handleBusinessTypeChange}
                isDisabled={isLocked}
              >
                <HStack spacing={8}>
                  <Radio value="new" isDisabled={isLocked}>
                    New
                  </Radio>
                  <Radio value="remarket" isDisabled={isLocked}>
                    Remarket
                  </Radio>
                </HStack>
              </RadioGroup>
              {validationErrors?.business_type && (
                <FormErrorMessage>
                  {validationErrors.business_type}
                </FormErrorMessage>
              )}
            </FormControl>
          )}
        </HStack>

        <HStack spacing={4} align="stretch">
          <Box flex={1}>
            <DateField
              label="Effective Date"
              date={parseEffectiveDate(effectiveDate)}
              onChange={handleEffectiveDateChange}
              required
              isDisabled={isLocked}
            />
          </Box>

          <FormControl
            flex={1}
            isRequired
            isInvalid={!!validationErrors?.binder_policy_number}
          >
            <FormLabel ml="0">Binder/Policy Number</FormLabel>
            <Input
              variant="main"
              value={binderPolicyNumber}
              onChange={handleBinderPolicyNumberChange}
              placeholder="Enter binder or policy number"
              isDisabled={isLocked}
              borderColor={!binderPolicyNumber ? 'red' : 'inherit'}
            />
            {validationErrors?.binder_policy_number && (
              <FormErrorMessage>
                {validationErrors.binder_policy_number}
              </FormErrorMessage>
            )}
          </FormControl>

          <FormControl
            flex={1}
            isRequired
            isInvalid={!!validationErrors?.premium}
          >
            <FormLabel ml="0">Premium</FormLabel>
            <NumberInput
              value={premiumInput}
              onChange={handlePremiumChange}
              min={0}
              step={0.01}
              precision={2}
              isDisabled={isLocked && !canEditPremium}
            >
              <NumberInputField
                placeholder="Enter premium amount"
                borderColor={!premium ? 'red' : 'inherit'}
              />
            </NumberInput>
            {validationErrors?.premium && (
              <FormErrorMessage>{validationErrors.premium}</FormErrorMessage>
            )}
            {premium > 10000 && (
              <Alert
                status="warning"
                mt={2}
                flexDirection="column"
                alignItems="flex-start"
              >
                <HStack mb={2}>
                  <AlertIcon />
                  <AlertDescription>
                    Premium amount exceeds $10,000. Please verify this amount is
                    correct.
                  </AlertDescription>
                </HStack>
                <Checkbox
                  ml={8}
                  isChecked={premiumConfirmed}
                  onChange={(e) =>
                    dispatch(setPremiumConfirmed(e.target.checked))
                  }
                  colorScheme="orange"
                  isRequired
                  isDisabled={isLocked}
                >
                  <Text fontWeight="bold">
                    I confirm the premium amount is correct
                  </Text>
                </Checkbox>
              </Alert>
            )}
          </FormControl>
        </HStack>

        <HStack spacing={4} align="stretch">
          <FormControl
            flex={1}
            isRequired
            isInvalid={!!validationErrors?.client_name}
          >
            <FormLabel ml="0">Client Name</FormLabel>
            <Input
              variant="main"
              value={clientName}
              onChange={handleClientNameChange}
              placeholder="Enter client name"
              isDisabled={isLocked}
              borderColor={!clientName ? 'red' : 'inherit'}
            />
            {validationErrors?.client_name && (
              <FormErrorMessage>
                {validationErrors.client_name}
              </FormErrorMessage>
            )}
          </FormControl>

          <FormControl
            flex={1}
            isRequired={!isLOA}
            isInvalid={!!validationErrors?.customer_code}
          >
            <FormLabel ml="0">Customer Code</FormLabel>
            <Input
              variant="main"
              value={customerCode}
              onChange={handleCustomerCodeChange}
              placeholder="Enter customer code"
              isDisabled={isLocked}
              borderColor={!customerCode && !isLOA ? 'red' : 'inherit'}
            />
            {validationErrors?.customer_code && (
              <FormErrorMessage>
                {validationErrors.customer_code}
              </FormErrorMessage>
            )}
          </FormControl>
        </HStack>

        {/* LOA-specific fields */}
        {isLOA && (
          <>
            <Divider my={4} />
            <Text fontWeight="bold" fontSize="lg" mb={2}>
              LOA Information
            </Text>
            <HStack spacing={4} align="stretch">
              <FormControl
                flex={1}
                isRequired
                isInvalid={!!validationErrors?.loa_prior_broker}
              >
                <FormLabel ml="0">Prior Broker Name</FormLabel>
                <Input
                  variant="main"
                  value={loaPriorBroker || ''}
                  onChange={handleLOAPriorBrokerChange}
                  placeholder="Enter prior broker name"
                  isDisabled={isLocked}
                  borderColor={!loaPriorBroker ? 'red' : 'inherit'}
                />
                {validationErrors?.loa_prior_broker && (
                  <FormErrorMessage>
                    {validationErrors.loa_prior_broker}
                  </FormErrorMessage>
                )}
              </FormControl>

              <FormControl
                flex={1}
                isRequired
                isInvalid={!!validationErrors?.loa_prior_policy_number}
              >
                <FormLabel ml="0">Prior Policy Number</FormLabel>
                <Input
                  variant="main"
                  value={loaPriorPolicyNumber || ''}
                  onChange={handleLOAPriorPolicyNumberChange}
                  placeholder="Enter prior policy number"
                  isDisabled={isLocked}
                  borderColor={!loaPriorPolicyNumber ? 'red' : 'inherit'}
                />
                {validationErrors?.loa_prior_policy_number && (
                  <FormErrorMessage>
                    {validationErrors.loa_prior_policy_number}
                  </FormErrorMessage>
                )}
              </FormControl>
            </HStack>
          </>
        )}

        <Divider my={6} />

        <Box>
          <Text fontWeight="bold" fontSize="lg" mb={4}>
            Billing Information
          </Text>

          <VStack spacing={4} align="stretch">
            <HStack spacing={4} align="stretch">
              <FormControl flex={1}>
                <FormLabel ml="0">Payment Method</FormLabel>
                <RadioGroup
                  value={billing.payment_method}
                  onChange={handlePaymentMethodChange}
                  isDisabled={isLocked}
                >
                  <HStack spacing={6}>
                    <Radio
                      value="agency_bill"
                      isDisabled={
                        isLocked ||
                        (requiresDirectBilling && !requiresAgencyBilling)
                      }
                    >
                      Agency Bill
                    </Radio>
                    <Radio
                      value="direct_bill"
                      isDisabled={
                        isLocked ||
                        (requiresAgencyBilling && !requiresDirectBilling)
                      }
                    >
                      Direct Bill
                    </Radio>
                  </HStack>
                </RadioGroup>
              </FormControl>

              {/* Payment Schedule — only relevant for Direct Bill */}
              {billing.payment_method !== 'agency_bill' && (
                <FormControl
                  flex={1}
                  isRequired
                  isInvalid={!!validationErrors?.billing?.payment_schedule}
                >
                  <FormLabel ml="0">Payment Schedule</FormLabel>
                  <Select
                    variant="main"
                    value={billing.payment_schedule}
                    onChange={handlePaymentScheduleChange}
                    placeholder="Select payment schedule"
                    isDisabled={isLocked}
                  >
                    <option value="Monthly">Monthly</option>
                    <option value="Yearly">Yearly</option>
                  </Select>
                  {validationErrors?.billing?.payment_schedule && (
                    <FormErrorMessage>
                      {validationErrors.billing.payment_schedule}
                    </FormErrorMessage>
                  )}
                </FormControl>
              )}
            </HStack>

            {/* Agency Bill fields — Province, Business Code, Fees */}
            {billing.payment_method === 'agency_bill' && (
              <>
                <HStack spacing={4} align="stretch">
                  <FormControl
                    flex={1}
                    isRequired
                    isInvalid={!!validationErrors?.billing?.province}
                  >
                    <FormLabel ml="0">Province</FormLabel>
                    <SelectSearch
                      options={PROVINCES}
                      value={billing.province}
                      onChange={handleBillingProvinceChange}
                      placeholder="Select province"
                      allowClear={true}
                      isDisabled={isLocked}
                    />
                    {validationErrors?.billing?.province && (
                      <FormErrorMessage>
                        {validationErrors.billing.province}
                      </FormErrorMessage>
                    )}
                  </FormControl>

                  <FormControl
                    flex={1}
                    isRequired
                    isInvalid={!!validationErrors?.billing?.business_code}
                  >
                    <FormLabel ml="0">Business Code</FormLabel>
                    <Input
                      variant="main"
                      value={billing.business_code}
                      onChange={handleBusinessCodeChange}
                      placeholder="Enter business code"
                      isDisabled={isLocked}
                      borderColor={!billing.business_code ? 'red' : 'inherit'}
                    />
                    {validationErrors?.billing?.business_code && (
                      <FormErrorMessage>
                        {validationErrors.billing.business_code}
                      </FormErrorMessage>
                    )}
                  </FormControl>
                </HStack>

                <HStack spacing={4} align="flex-end">
                  <FormControl flex={1}>
                    <FormLabel ml="0">Company Fees</FormLabel>
                    <NumberInput
                      value={companyFeesInput}
                      onChange={handleCompanyFeesChange}
                      min={0}
                      step={0.01}
                      precision={2}
                      isDisabled={isLocked}
                    >
                      <NumberInputField placeholder="0.00" />
                    </NumberInput>
                  </FormControl>

                  <FormControl flex={1}>
                    <FormLabel ml="0">Broker Fees</FormLabel>
                    <NumberInput
                      value={brokerFeesInput}
                      onChange={handleBrokerFeesChange}
                      min={0}
                      step={0.01}
                      precision={2}
                      isDisabled={isLocked}
                    >
                      <NumberInputField placeholder="0.00" />
                    </NumberInput>
                  </FormControl>

                  <FormControl flex={1}>
                    <FormLabel ml="0">Taxes</FormLabel>
                    <NumberInput
                      value={taxesInput}
                      onChange={handleTaxesChange}
                      min={0}
                      step={0.01}
                      precision={2}
                      isDisabled={isLocked}
                    >
                      <NumberInputField placeholder="0.00" />
                    </NumberInput>
                  </FormControl>
                </HStack>

                <HStack spacing={4} align="flex-end">
                  <FormControl flex={2}>
                    {/* Empty space for alignment */}
                  </FormControl>

                  <FormControl flex={1}>
                    <FormLabel ml="0" fontWeight="bold">
                      Total
                    </FormLabel>
                    <NumberInput
                      value={billing.total}
                      isReadOnly
                      min={0}
                      precision={2}
                    >
                      <NumberInputField
                        placeholder="0.00"
                        bg="gray.50"
                        fontWeight="bold"
                      />
                    </NumberInput>
                  </FormControl>
                </HStack>
              </>
            )}
          </VStack>
        </Box>
      </VStack>
    </Card>
  );
};

export default SpecialtyPolicyInformation;
